import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack, Box } from '@mui/material'
import {
  ObservationLogFormData,
  ObservationLogFormActions,
} from '@/components/forms'
import {
  ControlledDateTimeField,
  ControlledTextField,
  ControlledSelect,
  ControlledMediaUpload,
} from '@/components/inputs'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAuth } from '@/hooks'
import { FormComponentProps, UserGroups } from '@/types'
import {
  BATS_BIRDS_COUNTING_METHOD_OPTIONS,
  BATS_SPECIE_OPTIONS,
  WIND_DIRECTION_OPTIONS,
} from '../fieldsOptions'
import { useApiErrorData } from '@/hooks/forms/useApiErrorData'
import { schema } from './validationSchema'

export const BatsFlightPathForm = ({
  onSubmit,
  defaultValues,
  isLoading,
  apiErrorData,
}: FormComponentProps<ObservationLogFormData>): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  const { hasPermission } = useAuth()

  const methods = useForm<ObservationLogFormData>({
    mode: 'onChange',
    defaultValues: {
      specie: '',
      amount: '',
      counting_method: '',
      wind_direction: '',
      direction: '',
      amount_per_15_min: '',
      notes: '',
      observed_at: '',
      photos: undefined,
      sound_recordings: undefined,
    },
    values: defaultValues,
    resolver: yupResolver(schema),
  })

  useApiErrorData<ObservationLogFormData>(apiErrorData, methods)

  const disabledForTheUser = !hasPermission([
    UserGroups.MANAGER,
    UserGroups.EMPLOYEE,
    UserGroups.DATA_CONTROLLER,
  ])

  return (
    <FormProvider {...methods}>
      <Stack mt={4} width="100%" alignItems="stretch" spacing={4}>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Box width="33%">
            <ControlledSelect
              control={methods.control}
              name="specie"
              label={t('observation-log.fieldLabels.specie')}
              options={BATS_SPECIE_OPTIONS}
              errorMessagePrefix=""
            />
          </Box>
          <Box width="20%">
            <ControlledTextField
              control={methods.control}
              name="amount"
              placeholder={t('observation-log.fieldLabels.amount')}
              type="number"
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
          <Box width="25%">
            <ControlledSelect
              control={methods.control}
              name="counting_method"
              label={t('observation-log.fieldLabels.countingMethod')}
              options={BATS_BIRDS_COUNTING_METHOD_OPTIONS}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
          <Box width="20%">
            <ControlledTextField
              control={methods.control}
              name="recording_number"
              placeholder={t('observation-log.fieldLabels.recordingNumber')}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
        </Stack>
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Box width="33%">
            <ControlledSelect
              control={methods.control}
              name="wind_direction"
              label={t('observation-log.fieldLabels.windDirection')}
              options={WIND_DIRECTION_OPTIONS}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
          <Box width="20%">
            <ControlledSelect
              control={methods.control}
              name="direction"
              label={t('observation-log.fieldLabels.direction')}
              options={WIND_DIRECTION_OPTIONS}
              disabled={disabledForTheUser}
              errorMessagePrefix=""
            />
          </Box>
        </Stack>
        <Box width="33%">
          <ControlledTextField
            control={methods.control}
            name="amount_per_15_min"
            label={t('observation-log.fieldLabels.amountPer15Min')}
            type="number"
            disabled={disabledForTheUser}
            errorMessagePrefix=""
          />
        </Box>
        <ControlledTextField
          control={methods.control}
          name="notes"
          placeholder={t('observation-log.fieldLabels.notes')}
          multiline={true}
          rows={3}
          inputProps={{
            style: {
              padding: 0,
            },
          }}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
        <ControlledDateTimeField
          control={methods.control}
          name="observed_at"
          label={t('observation-log.fieldLabels.observedAt')}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
        <ControlledMediaUpload
          name="photos"
          control={methods.control}
          label={t('observation-log.fieldLabels.media')}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
        <ControlledMediaUpload
          name="sound_recordings"
          control={methods.control}
          accept="audio/wav,audio/x-wav"
          soundRecord={true}
          label={t('observation-log.fieldLabels.soundRecords', {
            maxNumber: 3,
          })}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />

        <ObservationLogFormActions
          onSubmit={onSubmit}
          defaultValues={defaultValues}
          isLoading={isLoading}
          methods={methods}
        />
      </Stack>
    </FormProvider>
  )
}
